<template lang="pug" src="./operator-editor.pug"></template>
<style lang="scss" src="./operator-editor.scss"></style>

<script>
import uuid from 'uuid';

import {mapGetters, mapState} from 'vuex';
import {validationMixin} from 'vuelidate';
import {alphaNum, email, maxLength, minLength, required} from 'vuelidate/lib/validators';

const rolesEnum = [
  'intern',
  'intern',
  'operator',
  'accountant',
  'sales-manager',
  'admin',
  'boss',
  'manager',
  'corporate-admin',
  'corporate-user',
  'leader',
  'admin-delivery',
  'admin-establishment',
  'manager-establishment'
];

import validate from '@/mixins/validate';

import request from '@/utils/request';

export default {
  mixins: [validationMixin, validate],
  data: () => ({
    anotherReportKey: uuid(),
    user: {
      firstName: null,
      lastName: null,
      middleName: null,
      phoneNumber: null,
      loginName: null,
      email: null,
      password: null,
      roleName: null,
      comment: null,
      brandId: null,
      establishmentId: null,
      establishmentAddressId: null,
    },
    operatorDetails: {
      canEditClients: false,
      canEditDrivers: false,
      canChangeOptionLevel: false,
      extendedCapabilities: false
    },
    reportIds: [],
    deliveryReportIds: [],
    deliveryReportCategories: [],
    establishmentAddresses: [],
    loaded: false,
    canSaveOperator: false,
    showPassword: false,
    removeModal: false,
    requestProcessing: false,
    reportsLoading: false
  }),
  validations: {
    user: {
      firstName: {
        required,
        minLength: minLength(2),
        maxLength: maxLength(30)
      },
      lastName: {
        required,
        minLength: minLength(2),
        maxLength: maxLength(30)
      },
      middleName: {
        minLength: minLength(2),
        maxLength: maxLength(30)
      },
      loginName: {
        required,
        alphaNum,
        minLength: minLength(2),
        maxLength: maxLength(30)
      },
      email: {
        email
      },
      roleName: {
        allowedRole(i) {
          return this.roles.includes(i);
        }
      },
      phoneNumber: {
        required
      },
      password: {
        required: function (value) {
          return this.user.passwordSalt ? true : !!(value && value.length);
        },
        minLength: minLength(6),
        maxLength: maxLength(30)
      },
      comment: {
        maxLength: maxLength(255)
      }
    }
  },
  computed: {
    ...mapState({
      brands: state => state.data.brands,
      establishments: state => state.data.establishments,
      allReports: state => state.reports.all,
      allCategories: state => state.reports.categories
    }),
    ...mapGetters(['roleName', 'userBrandId', 'operator']),
    saveMethod() {
      return this.user.id ? 'put' : 'post';
    },
    saveUrl() {
      const userId = this.user.id;

      return userId ? `/operators/${userId}` : '/operators';
    },
    requiredPassword() {
      return this.user.passwordSalt ? '' : '*';
    },
    roles() {
      return rolesEnum.filter(this.getAllowedRoles);
    },
    isMainRolePlus() {
      return !!['accountant', 'manager'].includes(this.roleName);
    },
    isBossRole() {
      return !!['boss'].includes(this.roleName);
    },
    isMainRole() {
      return !!['admin', 'corporate-admin'].includes(this.roleName);
    },
    isManagerRole() {
      return !!['manager'].includes(this.roleName);
    },
    canEditIfRemoved() {
      return (this.operatorDetails.operatorStatus === 'removed' && (this.isMainRole ||
          (this.isMainRolePlus && (this.user.roleName === 'operator' || this.user.roleName === 'intern')) ||
          (this.isBossRole && !['admin', 'corporate-admin'].includes(this.user.roleName)) ||
          (this.isManagerRole && !['admin', 'boss', 'corporate-admin'].includes(this.user.roleName))
      ));
    },
    canEdit() {
      return this.operatorDetails.operatorStatus !== 'removed' &&
          (this.isMainRole ||
              (this.isMainRolePlus && (this.user.roleName === 'operator' || this.user.roleName === 'intern')) ||
              (this.isBossRole && !['admin', 'corporate-admin'].includes(this.user.roleName)) ||
              (this.isManagerRole && !['admin', 'boss', 'corporate-admin'].includes(this.user.roleName)) ||
              (['admin-delivery', 'admin-establishment'].includes(this.roleName))
          );
    },
    showCanEditClients() {
      return !['admin', 'boss', 'manager', 'admin-delivery', 'manager-establishment', 'admin-establishment'].includes(this.user.roleName);
    },
    showCanChangeOptionLevel() {
      return !['admin', 'boss', 'manager', 'admin-delivery', 'manager-establishment', 'admin-establishment'].includes(this.user.roleName);
    },
    showBossAdditionalFields() {
      return ['boss'].includes(this.user.roleName);
    },
    showReportsCategories() {
      return ['admin'].includes(this.roleName);
    },
    showReports() {
      return this.allCategories
          && this.allCategories.length
          && this.showReportsCategories
          && this.user.id;
    },
    showDeliveryReports() {
      return ['admin', 'admin-delivery', 'admin-establishment'].includes(this.user.roleName);
    },
    reportsCategories() {
      return this.allCategories.map(category => ({
        ...category,
        title: category.name || this.$vuetify.lang.t('$vuetify.pages.Reports.another')
      }));
    },
    brandedEstablishments() {
      return this.user.brandId ? (this.operator.user.establishmentId ? [this.establishments.find(i => +i.id === +this.operator.user.establishmentId)] : this.establishments.filter(i => +i.brandId === +this.user.brandId)) : [];
    }
  },
  watch: {
    operatorDetails: {
      handler() {
        this.canSaveOperator = true;
      },
      deep: true
    },
    user: {
      handler() {
        this.canSaveOperator = true;
      },
      deep: true
    },
    reportIds() {
      this.canSaveOperator = true;
    },
    deliveryReportIds() {
      this.canSaveOperator = true;
    },
    async 'user.establishmentId'(establishmentId) {
      if (establishmentId) {
        await request({method: 'get', url: `/delivery/establishment/${establishmentId}/addresses`})
            .then(res => {
              if (res.data) {
                this.establishmentAddresses = res.data;

                if (!res.data.find(i => i.id === this.user.establishmentAddressId)) {
                  this.user.establishmentAddressId = null;
                }
              }
            }).catch(error => {
              if (error.response && error.response.data && error.response.data.error) {
                this.$store.dispatch('alert/addMessage', {
                  message: error.response.data.error
                }, {root: true});
              }
            });
      } else {
        this.establishmentAddresses = [];
        this.user.establishmentAddressId = null;
      }
    }
  },
  created() {
    this.$store.dispatch('data/getBrands');
    this.$store.dispatch('data/getEstablishments');

    this.loadDeliveryReports();

    if (['corporate-admin'].includes(this.roleName)) {
      this.user.roleName = 'corporate-user';
    } else if (['admin-delivery'].includes(this.roleName)) {
      this.user.roleName = 'admin-establishment';
    } else if (['admin-establishment'].includes(this.roleName)) {
      this.user.roleName = 'manager-establishment';
    } else {
      this.user.roleName = 'operator';
    }

    if (this.userBrandId) {
      this.user.brandId = this.userBrandId;
    }

    if (this.$route.params.id) {
      this.loaded = true;
      this.loadData();
    }
  },
  beforeDestroy() {
    this.$store.commit('reports/SET_CATEGORIES', []);
  },
  methods: {
    async loadDeliveryReports() {
      await request({method: 'get', url: `/delivery-reports`})
          .then(res => {
            const categories = [];

            res.data.forEach(report => {
              let categoryIndex = categories.findIndex(c => c.id === report.deliveryReportCategoryId);

              if (categoryIndex === -1) {
                categories.push({
                  id: report.deliveryReportCategoryId,
                  translation: this.$vuetify.lang.t('$vuetify.pages.DeliveryReports.categories.' + report.categoryName),
                  reports: []
                });
                categoryIndex = categories.length - 1;
              }

              categories[categoryIndex].reports.push(report);
            });

            this.deliveryReportCategories = categories;
          }).catch(error => {
            if (error.response && error.response.data && error.response.data.error) {
              this.$store.dispatch('alert/addMessage', {
                message: error.response.data.error
              });
            }
          });
    },
    getIconForAllDeliveryReports(current, max) {
      if (current === 0) {
        return 'mdi-checkbox-blank-outline';
      } else if (current < max) {
        return 'mdi-minus-box';
      } else {
        return 'mdi-close-box';
      }
    },
    toggleDeliveryReportCategory(categoryId) {
      const indexOfCategory = this.deliveryReportCategories.findIndex(i => i.id === categoryId);
      const reportNames = this.deliveryReportCategories[indexOfCategory].reports.map(i => i.id);

      if (this.deliveryReportIds.some(i => reportNames.includes(i))) {
        this.deliveryReportIds = this.deliveryReportIds.filter(i => !reportNames.includes(i));
      } else {
        this.deliveryReportIds.push(...reportNames);
      }
    },
    async loadData() {
      await request({method: 'get', url: `/operators/${this.$route.params.id}`})
          .then(res => this.successLoad(res)).catch(error => {
            if (error.response && error.response.data && error.response.data.error) {
              this.$store.dispatch('alert/addMessage', {
                message: error.response.data.error
              });
            }
          }).finally(() => this.loaded = false);
    },

    getReportCategories() {
      if (this.user.id) {
        this.$store.dispatch('reports/getReportsCategories', {
          userId: this.user.id
        });
      }
    },

    async saveOperator() {
      if (this.$v.user.$invalid) {
        this.$v.user.$touch();

        this.$nextTick(() => this.focusFirstErrorInput());
        return;
      }

      if (this.requestProcessing) return;

      this.requestProcessing = true;

      const data = Object.assign({}, {
        user: this.user,
        operatorDetails: this.operatorDetails,
        reportIds: this.reportIds,
        deliveryReportIds: this.deliveryReportIds
      });

      await request({method: this.saveMethod, url: this.saveUrl, data})
          .then(res => {
            this.successLoad(res);
            this.$store.dispatch('alert/addMessage', {
              type: 'success',
              message: 'success'
            });
            this.$v.user.$reset();
          }).catch(error => {
            if (error.response && error.response.data && error.response.data.error) {
              this.$store.dispatch('alert/addMessage', {
                message: error.response.data.error
              });
            }
          }).finally(() => {
            this.$nextTick(() => {
              this.requestProcessing = false;
            });
          });
    },

    successLoad(res) {
      const {data} = res;

      this.operatorDetails = Object.assign({}, {
        ...Object.select(data, [
          'id', 'canEditClients', 'canEditDrivers',
          'operatorStatus', 'canChangeOptionLevel',
          'extendedCapabilities'
        ])
      });

      this.reportIds = res.data.reportIds || [];
      this.deliveryReportIds = res.data.deliveryReportIds || [];

      this.user = Object.assign({}, Object.select(data.user, [
        'id', 'passwordSalt', ...Object.keys(this.user)
      ]));

      if (this.user.id && !this.$route.params.id) {
        this.$router.push(`/operators/editor/${this.user.id}`);
      }

      this.getReportCategories();

      this.$nextTick(() => {
        this.canSaveOperator = false;
      });
    },

    getAllowedRoles: function (role) {
      return (['client', 'driver', 'operator', 'accountant', 'manager', 'sales-manager', 'intern', 'corporate-user'].includes(this.roleName) &&
              ['intern', 'operator', 'accountant', 'manager', 'sales-manager'].includes(role)) ||
          (['admin'].includes(this.roleName) && [
            'intern', 'operator',
            'accountant', 'manager',
            'boss', 'sales-manager',
            'corporate-admin', 'admin', 'leader',
            'admin-delivery',
            'admin-establishment',
            'manager-establishment'
          ].includes(role)) ||
          (['boss'].includes(this.roleName) &&
              ['intern', 'operator', 'accountant', 'manager', 'boss', 'sales-manager', 'leader'].includes(role)) ||
          (['corporate-admin'].includes(this.roleName) && ['corporate-user'].includes(role)) ||
          (['admin-delivery'].includes(this.roleName) && ['admin-delivery', 'admin-establishment', 'manager-establishment'].includes(role)) ||
          (['admin-establishment'].includes(this.roleName) && ['admin-establishment', 'manager-establishment'].includes(role)) ||
          (['manager-establishment'].includes(this.roleName) && ['manager-establishment'].includes(role));
    },

    async remove() {
      if (this.requestProcessing) return;

      this.requestProcessing = true;

      await request({method: 'delete', url: `/operators/${this.user.id}`})
          .then(res => {
            if (res.status == 200) {
              this.$router.push('/users/operators');
            }
          }).catch(error => {
            if (error.response && error.response.data && error.response.data.error) {
              this.$store.dispatch('alert/addMessage', {
                message: error.response.data.error
              });
            }
          }).finally(() => {
            this.$nextTick(() => {
              this.requestProcessing = false;
              this.removeModal = false;
            });
          });
    },

    async hireBack() {
      await request({method: 'post', url: `/operators/${this.user.id}/hire-back`})
          .then(res => {
            this.successLoad(res);
            this.$store.dispatch('alert/addMessage', {
              type: 'success',
              message: 'success'
            });
          }).catch(error => {
            if (error.response && error.response.data && error.response.data.error) {
              this.$store.dispatch('alert/addMessage', {
                message: error.response.data.error
              });
            }
          });
    },

    selectAllReports() {
      const reports = this.allReports;

      return reports && reports.every(it => this.reportIds.includes(it.id));
    },

    selectSomeReports() {
      const reports = this.allReports;

      return reports && reports.some(it => this.reportIds.includes(it.id));
    },

    getIcon(selectAll, selectSome) {
      if (selectAll) {
        return 'mdi-close-box';
      }

      if (selectSome) {
        return 'mdi-minus-box';
      }

      return 'mdi-checkbox-blank-outline';
    },

    getIconForReport(reportId) {
      return this.reportIds.includes(reportId) ? 'mdi-check' : 'mdi-checkbox-blank-outline';
    },

    getIconForDeliveryReport(reportId) {
      return this.deliveryReportIds.includes(reportId) ? 'mdi-check' : 'mdi-checkbox-blank-outline';
    },

    expandReportsCategory(event, {name}) {
      this.isActiveCategory = !this.isActiveCategory;
      const category = name || null;

      if (this.allReports.map('category').includes(category)) {
        return;
      }

      if (this.reportsLoading) return;

      this.reportsLoading = true;

      this.$store.dispatch('reports/getAllReports', {
        category
      }).finally(() => {
        this.$nextTick(() => {
          this.scrollTo(event);
          this.reportsLoading = false;
        });
      });
    },

    scrollTo(event) {
      event.target.scrollIntoView({behavior: "smooth"});
    },

    toggleReportId(reportId, reportCategory) {
      const activeCategory = this.reportsCategories.find({name: reportCategory || ''});

      if (!activeCategory) return;

      const includeReport = this.reportIds.includes(reportId);

      if (includeReport) {
        activeCategory.count--;
        return this.reportIds = this.reportIds.filter(id => id != reportId);
      }

      activeCategory.count++;
      return this.reportIds.push(reportId);
    },

    toggleReportCategory(category) {
      const categoryReportsIds = this.allReports.map('id');
      const activeCategory = this.reportsCategories.find({name: category});

      if (!activeCategory) return;

      if (this.selectAllReports(category)) {
        activeCategory.count = 0;
        return this.reportIds = this.reportIds.filter(id => !categoryReportsIds.includes(id));
      }

      activeCategory.count = categoryReportsIds.length;

      if (this.selectSomeReports(category)) {
        const uncheckedReports = this.allReports.map('id')
            .filter(id => !this.reportIds.includes(id));

        return this.reportIds.push(...uncheckedReports);
      }

      return this.reportIds.push(...categoryReportsIds);
    },

    toggleDeliveryReport(reportId) {
      if (this.deliveryReportIds.includes(reportId)) {
        this.deliveryReportIds = this.deliveryReportIds.filter(i => i !== reportId);
      } else {
        this.deliveryReportIds.push(reportId);
      }
    }
  }
};
</script>
